import React from "react";
import {HugeiconsIcon} from "@hugeicons/react";
import {GitCompareIcon} from "@hugeicons/core-free-icons";
import {sidebarMenuItems} from "../project/flow/initialState";
import {RegistryDashboardPage} from "./dashboard/RegistryDashboardPage";
import {RegistrySettingsPage} from "./settings/RegistrySettingsPage";
import {RegistryDialplanPage} from "./dialplan/RegistryDialplanPage";
import {RegistrySupervisionPage} from "./supervision/RegistrySupervisionPage";
import {RegistryCampaignsPage} from "./campaigns/RegistryCampaignsPage";
import {RegistryMessageAnnotationsPage} from "./message-annotation/RegistryMessageAnnotationsPage";
import {RegistryVocabularyPage} from "./vocabulary/RegistryVocabularyPage";
import {RegistryDesignerPage} from "./designer/RegistryDesignerPage";
import {RegistryMCPDiscoveryPage} from "./mcp-discovery/RegistryMCPDiscoveryPage";
import {RegistryKnowledgeManagerPage} from "./knowledge-manager/RegistryKnowledgeManagerPage";
import {RegistryVersionControlPage} from "./version-control/RegistryVersionControlPage";
// The registry project uses the same menu as the flow layout. Legacy items build their links from a
// Firebase project (`/project/{id}/...`), so they are converted to paths relative to
// `/registry-project/{registryProjectId}/{subProjectId}`.
const PLACEHOLDER_ID = "__registry_project__";

const toRelativePath = (item) => item.to({id: PLACEHOLDER_ID})
  .replace(`/project/${PLACEHOLDER_ID}`, "")
  .replace(/^\//, "");

export const joinPath = (basePath, path) => path ? `${basePath}/${path}` : basePath;

const toRegistryItem = (item) => ({
  ...item,
  path: item.to ? toRelativePath(item) : undefined,
  // menu context: {basePath, project} where project is the registry ProjectDTO
  to: item.to ? (context) => joinPath(context?.basePath, toRelativePath(item)) : undefined,
  // legacy checks read Firebase settings, so map the registry project onto that shape
  disabled: item.disabled
    ? (context) => item.disabled({settings: {organization: context?.project?.organizationId}})
    : undefined,
  submenu: item.submenu?.map(toRegistryItem),
});

// Pages only registry projects have (the flow layout has no route for them), in the legacy shape so they go through
// toRegistryItem like the rest; placed before Settings
const registryOnlyMenuItems = [
  {
    label: "Version control",
    icon: <HugeiconsIcon icon={GitCompareIcon} size={20} strokeWidth={1.5} color="currentColor"/>,
    to: (project) => `/project/${project?.id}/version-control`,
    roles: ["ADMIN", "MANAGER"]
  },
];

const settingsIndex = sidebarMenuItems.findIndex(item => item.label === "Settings");
const insertAt = settingsIndex < 0 ? sidebarMenuItems.length : settingsIndex;

export const registrySidebarMenuItems = [
  ...sidebarMenuItems.slice(0, insertAt),
  ...registryOnlyMenuItems,
  ...sidebarMenuItems.slice(insertAt),
].map(toRegistryItem);

// One route per menu entry (submenus flattened); "" is the index (Dashboard)
// A page can have several menu entries (e.g. Vocabulary: top level for agents, under Settings for admins and
// managers); they share one route that allows the roles of all of them
export const registryProjectRoutes = Object.values(registrySidebarMenuItems
  .flatMap(item => item.submenu ? item.submenu : [item])
  .filter(item => item.path !== undefined)
  .reduce((routes, {label, path, roles}) => {
    const route = routes[path];
    routes[path] = route
      ? {...route, roles: [...new Set([...route.roles, ...roles])]}
      : {label, path, roles};
    return routes;
  }, {}));

// Pages rebuilt on the Project API, by route path ("" is the dashboard). Routes without an entry show a
// placeholder. Pages read the project, selected sub project and role with useRegistryProject().
export const registryPages = {
  "": RegistryDashboardPage,
  "settings": RegistrySettingsPage,
  "dialplan": RegistryDialplanPage,
  "supervision": RegistrySupervisionPage,
  "campaigns": RegistryCampaignsPage,
  "message-annotations": RegistryMessageAnnotationsPage,
  "vocabulary": RegistryVocabularyPage,
  "flow": RegistryDesignerPage,
  "mcp-registry": RegistryMCPDiscoveryPage,
  "knowledge-manager": RegistryKnowledgeManagerPage,
  "version-control": RegistryVersionControlPage,
};
